import type { DatepickerAvailabilityResultsDay } from '~/apps/datepicker-availability/types'
import type { ChargetypeCalendarDay } from '~/apps/chargetype-calendar/schemas'
import type {
  UnifiedCalendarResponse,
  AvailabilityCalendarResponse,
  PricingCalendarResponse,
  AvailabilityDay,
} from './schemas'

/**
 * Transforms availability calendar API response to datepicker availability format
 * This allows the new API to work with existing components
 */
export function transformToDatepickerAvailability(
  response: AvailabilityCalendarResponse | UnifiedCalendarResponse,
): DatepickerAvailabilityResultsDay[] {
  const availability: DatepickerAvailabilityResultsDay[] = []

  Object.entries(response.calendar_data).forEach(([date, dayData]) => {
    // In the new API structure, all dates in calendar_data are arrival dates
    // No need to check available_as_arrival anymore
    const dayInfo: DatepickerAvailabilityResultsDay = {
      date,
      bookable_dates: dayData.bookable_departures,
    }

    // Include pricing information if available
    if (dayData.price) {
      dayInfo.price = dayData.price
    }
    if (dayData.price_fx) {
      dayInfo.price_fx = dayData.price_fx
    }
    if (dayData.nights !== undefined) {
      dayInfo.nights = dayData.nights
    }
    if (dayData.min_days !== undefined) {
      dayInfo.min_days = dayData.min_days
    }

    availability.push(dayInfo)
  })

  // Sort by date
  availability.sort((a, b) => a.date.localeCompare(b.date))

  return availability
}

/**
 * Transforms calendar API response to chargetype calendar format
 * Maintains compatibility with existing XState machine and components
 */
export function transformToChargetypeCalendar(
  response: AvailabilityCalendarResponse | UnifiedCalendarResponse,
): { [key: string]: ChargetypeCalendarDay } {
  const calendarData: { [key: string]: ChargetypeCalendarDay } = {}

  Object.entries(response.calendar_data).forEach(([date, dayData]) => {
    // In the new API, all dates in calendar_data are available arrival dates
    const chargetypeDay: ChargetypeCalendarDay = {
      fulldate: date,
      available: true, // All dates in calendar_data are available arrivals
    }

    // Add pricing data if available
    if (dayData.price_fx) {
      chargetypeDay.priceFx = dayData.price_fx
    }

    if (dayData.nights !== undefined) {
      chargetypeDay.nights = dayData.nights
    }

    // Add minimum stay requirements from the backend
    if (dayData.min_days !== undefined) {
      chargetypeDay.minDays = dayData.min_days
    }

    if (dayData.min_days_violated !== undefined) {
      chargetypeDay.minDaysViolated = dayData.min_days_violated
    }

    calendarData[date] = chargetypeDay
  })

  return calendarData
}

/**
 * Calendar API v2 - Merges availability and pricing data according to the implementation guide
 * 1. Start with availability data (all bookable dates with min prices)
 * 2. Override with exact pricing where available for the selected duration
 * 3. Fallback to min stay pricing for dates not available for the selected duration
 */
export function mergeAvailabilityAndPricing(
  availabilityData: AvailabilityCalendarResponse,
  pricingData?: PricingCalendarResponse,
  selectedNights?: number,
): AvailabilityCalendarResponse {
  // Create pricing lookup for fast access

  const pricingLookup: { [date: string]: any } = {}
  if (pricingData && selectedNights) {
    pricingData.pricing_data.forEach((stay) => {
      pricingLookup[stay.arrive_date] = {
        price: stay.price,
        price_fx: stay.price_fx,
        nights: stay.nights,
        isExactMatch: true,
      }
    })
  }

  // Merge calendar data
  const mergedCalendarData: { [date: string]: AvailabilityDay } = {}

  Object.entries(availabilityData.calendar_data).forEach(
    ([date, availabilityInfo]) => {
      // In the new API, all dates in calendar_data are arrival dates
      if (pricingLookup[date]) {
        // Use exact pricing for selected duration
        mergedCalendarData[date] = {
          ...availabilityInfo,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Pricing lookup contains validated pricing data
          price: pricingLookup[date].price, // Use exact price from pricing endpoint
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Pricing lookup contains validated pricing data
          price_fx: pricingLookup[date].price_fx, // Use exact price_fx from pricing endpoint
          nights: selectedNights || availabilityInfo.nights, // Use selected nights
        }
      } else {
        // Keep minimum stay pricing from availability
        mergedCalendarData[date] = availabilityInfo
      }
    },
  )

  // Return merged response
  return {
    pitchtype_id: availabilityData.pitchtype_id,
    window: availabilityData.window,
    calendar_data: mergedCalendarData,
    meta: availabilityData.meta,
  }
}

/**
 * Creates a context object compatible with the existing chargetype calendar context
 */
export function createChargetypeContext(
  response: AvailabilityCalendarResponse | UnifiedCalendarResponse,

  params: any,
): any {
  const calendarSliceData = transformToChargetypeCalendar(response)

  return {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Params structure varies by chargetype context
    params,
    calendarSliceData,
  }
}
